import { useRoute } from "@/components/shared/router";
import { Typography } from "@/components/shared/typography";
import { Badge, Button, Card, CardContent, Image, View } from "@/components/ui";
import { copyClipboard } from "@/lib/helpers/copy-clipboard";
import { formatDate } from "@/lib/helpers/date";

import { useShareGiftCard } from "../share-gift-card-modal";
import {
  formatGiftCardClientNameHide,
  getGiftCardOccasionIcon,
  GIFT_CARD_STATUS_CONFIG,
} from "./lib";
import { GiftCardStatus } from "./types";
import type { GiftCardHistoryItem as GiftCardHistoryItemType } from "./types";

interface GiftCardHistoryItemSentProps {
  item: GiftCardHistoryItemType;
}

export const GiftCardHistoryItemSent = ({ item }: GiftCardHistoryItemSentProps) => {
  const { push } = useRoute();
  const icon = getGiftCardOccasionIcon(item.occasion.key);
  const statusConfig = GIFT_CARD_STATUS_CONFIG[item.status];
  const giftCode = item.code;

  const { share } = useShareGiftCard();

  const getSubtitle = () => {
    if (item.status === GiftCardStatus.Active && !item.recipientName) {
      return "Waiting for recipient to redeem";
    }
    if (item.status === GiftCardStatus.Expired) {
      return "This gift has expired";
    }
    const nameWithAsterisk = formatGiftCardClientNameHide(item.recipientName);
    const prefixMap: Record<string, string> = {
      "sent-REDEEMED": "Redeemed by",
      "sent-PENDING_PAYMENT": "Pending payment",
      "sent-DEFAULT": "Sent to",
    };
    const key = `sent-${item.status}`;
    const prefix = prefixMap[key] || prefixMap["sent-DEFAULT"];
    return `${prefix} ${item.status !== GiftCardStatus.PendingPayment ? nameWithAsterisk : ""}`;
  };

  const handlePress = () => {
    push({
      pageKey: "giftCardDetail",
      params: { id: item.id },
    });
  };

  const handleShareClick = (event?: React.BaseSyntheticEvent) => {
    event?.stopPropagation?.();
    share(giftCode);
  };

  return (
    <View className="flex flex-col p-4" onClick={handlePress}>
      <View className="flex flex-row items-start gap-3">
        <Image src={icon} width={32} height={32} className="size-8" />
        <View className="flex flex-1 flex-col gap-1">
          <View className="flex flex-row items-center justify-between">
            <Typography variant="label-sm">{item.messageTitle}</Typography>
            <Badge variant={statusConfig.variant} outline={false}>
              {statusConfig.label}
            </Badge>
          </View>
          <View className="flex flex-row items-center gap-2">
            <Typography variant="label-sm" color="brand-primary">
              ${item.amount}
            </Typography>
          </View>
          <Typography variant="body-sm">{getSubtitle()}</Typography>
          <Typography variant="caption-md">
            {formatDate(new Date(item.createdAt), "d MMM yyyy, hh:mm a", true)}
          </Typography>
          {item.status === GiftCardStatus.Active && (
            <View className="flex flex-row gap-2">
              <Card radius="xl" className="flex-1">
                <CardContent className="flex flex-row items-center justify-between p-3">
                  <Typography
                    variant="caption-md"
                    color="foreground-intermediate"
                    className="native:hidden"
                  >
                    Gift Code
                  </Typography>
                  <Typography variant="body-sm" numberOfLines={1}>
                    {giftCode}
                  </Typography>
                </CardContent>
              </Card>
              <Button
                variant="secondary"
                size="sm"
                color="CTA2"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  copyClipboard(giftCode);
                }}
              >
                Copy
              </Button>
              <Button
                variant="primary"
                size="sm"
                color="CTA"
                onClick={handleShareClick}
                iconAlignment="end"
                iconName="shareFat"
                iconColor="white"
              >
                Share
              </Button>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};
